import styled from "styled-components";

/* ── Page Header ─────────────────────────────────── */

export const PageHeader = styled.header`
  background: var(--surface);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1rem 0 0;
  position: relative;
  z-index: 10;
`;

export const Breadcrumb = styled.nav`
  font-size: 0.8125rem;
  color: var(--text-label);
  margin-bottom: 0.25rem;

  a {
    color: var(--primary);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span { margin: 0 0.375rem; }
`;

export const PageTitle = styled.h1`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  color: var(--text-label);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 0.375rem;
  transition: color 0.15s ease, background 0.15s ease;
  font-size: 1.25rem;

  &:hover {
    color: var(--text-secondary);
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.875rem;
  flex-wrap: wrap;
`;

/* ── Info Card ───────────────────────────────────── */

export const InfoCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 1rem;
`;

export const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

export const InfoCardTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin: 0;
`;

export const InfoCardBody = styled.div`
  padding: 1rem 1.25rem;
`;

/* ── Info Grid (definition list replacement) ─────── */

export const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1.5rem;
  margin: 0;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
`;

export const InfoItemFull = styled.div`
  grid-column: 1 / -1;
`;

export const InfoItem = styled.div``;

export const InfoLabel = styled.dt`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-label);
  margin-bottom: 0.125rem;
`;

export const InfoValue = styled.dd`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const InfoValueMuted = styled(InfoValue)`
  color: var(--text-label);
  font-weight: 400;
  font-style: italic;
`;

export const InfoValueDanger = styled(InfoValue)`
  color: var(--danger);
`;

/* ── Procedure Box ───────────────────────────────── */

export const ProcedureBox = styled.div`
  background: var(--surface-2);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: var(--border-radius-sm);
  padding: 0.875rem 1rem;
`;

export const ProcedureDescription = styled.p`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem;
`;

export const ProcedureDate = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  i { font-size: 0.9375rem; }
`;

/* ── Payment Summary ─────────────────────────────── */

export const PaymentSummary = styled.div`
  background: var(--surface-2);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
`;

export const PaymentMainValue = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  .label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-label);
    margin-bottom: 0.25rem;
  }

  .amount {
    font-size: 1.625rem;
    font-weight: 800;
    color: var(--primary);
    line-height: 1;
  }

  .total-amount {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-secondary);
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;

    .total-label {
      font-size: 0.75rem;
      color: var(--text-label);
      font-weight: 500;
    }
  }
`;

export const PaymentDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem 1.25rem;
  gap: 0.5rem 1rem;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentDetailItem = styled.div`
  .pd-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-label);
    margin-bottom: 0.0625rem;
  }

  .pd-value {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-secondary);
  }

  .pd-value-danger {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--danger);
    display: flex;
    align-items: center;
    gap: 0.2rem;
  }
`;

/* ── Pending Alert ───────────────────────────────── */

export const PendingAlert = styled.div`
  background: rgba(220, 53, 69, 0.05);
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-left: 4px solid var(--danger);
  border-radius: var(--border-radius-sm);
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;

  .pending-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    color: var(--danger);
    font-size: 0.875rem;
    margin-bottom: 0.5rem;

    i { font-size: 1.1rem; }
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;

    li {
      font-size: 0.875rem;
      color: var(--text-secondary);
      padding: 0.25rem 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
  }
`;

/* ── Section Divider ─────────────────────────────── */

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  h6 {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-label);
    margin: 0;
  }
`;

/* ── Stepper (substitui a timeline Bootstrap) ──────── */

export const Stepper = styled.nav`
  background: var(--surface);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1.375rem 0 1.25rem;
`;

export const StepperList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0 1.5rem;
  display: flex;
  align-items: flex-start;
`;

export const StepConnector = styled.span<{ $filled: boolean }>`
  flex: 1;
  height: 2px;
  min-width: 8px;
  align-self: flex-start;
  margin-top: 17px;
  background: ${({ $filled }) =>
    $filled ? "var(--primary)" : "rgba(0,0,0,0.1)"};
  transition: background 0.3s ease;

  body.dark & {
    background: ${({ $filled }) =>
      $filled ? "var(--primary)" : "rgba(255,255,255,0.12)"};
  }
`;

export const StepItem = styled.li<{ $state: "done" | "active" | "upcoming" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 72px;

  @media (max-width: 575.98px) {
    min-width: 48px;
  }
`;

export const StepDot = styled.div<{ $state: "done" | "active" | "upcoming" }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  transition: background 0.25s ease, box-shadow 0.25s ease;
  font-size: 1.125rem;

  background: ${({ $state }) =>
    $state === "upcoming" ? "var(--surface-3)" : "var(--primary)"};

  color: ${({ $state }) =>
    $state === "upcoming" ? "var(--text-label)" : "#fff"};

  box-shadow: ${({ $state }) =>
    $state === "active"
      ? "0 0 0 5px rgba(46,209,124,0.18), 0 0 0 1px rgba(46,209,124,0.4)"
      : "none"};

  @media (max-width: 575.98px) {
    width: 28px;
    height: 28px;
    font-size: 0.875rem;
  }
`;

export const StepBadge = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: var(--danger);
  border: 2px solid var(--surface);
`;

export const StepLabel = styled.span<{ $state: "done" | "active" | "upcoming" }>`
  font-size: 0.6875rem;
  font-weight: ${({ $state }) => ($state === "active" ? "700" : "500")};
  color: ${({ $state }) => {
    if ($state === "active") return "var(--primary)";
    if ($state === "done") return "var(--text-secondary)";
    return "var(--text-label)";
  }};
  text-align: center;
  line-height: 1.3;
  transition: color 0.2s ease;

  @media (max-width: 575.98px) {
    font-size: 0.5625rem;
    max-width: 56px;
  }
`;

/* ── SectionCard (substitui form-box Bootstrap) ───── */

export const SectionCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 1rem;
`;

export const SectionCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.875rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

export const SectionCardTitle = styled.h5`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i { color: var(--text-muted); font-size: 1.1rem; }
`;

export const SectionCardBody = styled.div`
  padding: 1.25rem;
`;

/* ── Split de Pagamento ──────────────────────────── */

export const SplitWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const SplitCard = styled.div`
  background: var(--surface-2);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover { box-shadow: var(--shadow-sm); }

  body.dark & {
    background: var(--surface-3);
    border-color: rgba(255, 255, 255, 0.07);
  }
`;

export const SplitCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  background: rgba(0, 0, 0, 0.025);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  body.dark & { background: rgba(255, 255, 255, 0.03); }
`;

export const SplitIndex = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SplitCardTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  flex: 1;
`;

export const SplitRemoveBtn = styled.button`
  background: none;
  border: none;
  padding: 0.2rem 0.4rem;
  color: var(--text-label);
  cursor: pointer;
  border-radius: 0.375rem;
  font-size: 1rem;
  line-height: 1;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover { color: var(--danger); background: rgba(220, 53, 69, 0.08); }
  &:focus-visible { outline: 2px solid var(--danger); outline-offset: 2px; }
`;

export const SplitFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  padding: 0.875rem;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
`;

export const SplitFieldFull = styled.div`
  grid-column: 1 / -1;
`;

export const SplitFieldLabel = styled.label`
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-label);
  margin-bottom: 0.25rem;
`;

export const SplitAddBtn = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1.5px dashed rgba(0, 0, 0, 0.15);
  border-radius: var(--border-radius);
  background: transparent;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: rgba(var(--primary-rgb), 0.04);
  }

  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  i { font-size: 1rem; }

  body.dark & { border-color: rgba(255, 255, 255, 0.15); }
`;

export const SplitTotalBar = styled.div<{ $valid: boolean; $hasData: boolean }>`
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  border: 1px solid ${({ $valid }) =>
    $valid ? "rgba(46,209,124,0.25)" : "rgba(220,53,69,0.2)"};
  display: ${({ $hasData }) => ($hasData ? "block" : "none")};
`;

export const SplitTotalTrack = styled.div`
  height: 5px;
  background: rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
`;

export const SplitTotalFill = styled.div<{ $pct: number; $valid: boolean }>`
  position: absolute;
  inset: 0 0 0 0;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $valid }) => ($valid ? "var(--primary)" : "var(--danger)")};
  transition: width 0.3s ease, background 0.3s ease;
`;

export const SplitTotalInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;

  .label { color: var(--text-muted); }

  .values {
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .total { color: var(--text-primary); }
  .sep   { color: var(--text-label); font-weight: 400; }
  .quote { color: var(--text-label); }
  .status-ok   { color: var(--primary); font-size: 0.75rem; }
  .status-diff { color: var(--danger);  font-size: 0.75rem; }
`;

export const SplitSaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 0.25rem;
`;
